import React, { useEffect } from 'react';
import { RoundToggle } from '@umanforge/common';

import SelectInput from '@common_components/inputs/selects/selectInput.component';
import InputText from '@common_components/inputs/textInput.component';
import type { ReadSectionProps } from './chartSectionList.interfaces';
import type { ChartReadEnd, ChartReadStart } from 'interfaces/chartOfSection.interface';
import { getReadCategory } from './chartReadRange.utils';
import { MAX_DELTA } from 'constants/chart.constants';

const PREFIX_TO_UNIT: Record<string, keyof typeof MAX_DELTA> = {
  'd': 'day', 'w': 'week', 'm': 'month', 'a': 'year', 'y': 'year',
};

const GAUGE_TYPES: string[] = [ 'progress_gauge', 'custom_gauge', 'auto_increment_gauge' ];

const DEFAULT_READ_END_BY_CATEGORY: Record<string, ChartReadEnd> = {
  d: 'd-15', w: 'w-5', m: 'm-3', y: 'y-5',
};

const ReadSection: React.FC<ReadSectionProps> = ( {
  t: translate,
  newSetting,
  onChangeField,
  fetchReadStartOptions,
  fetchReadEndOptions,
} ) => {
  const isGauge = GAUGE_TYPES.includes( newSetting.type );
  const isAutoIncrementGauge = newSetting.type === 'auto_increment_gauge';
  const readPrefix = newSetting.readStart?.charAt( 0 ) ?? '';
  const rangeUnit = PREFIX_TO_UNIT[ readPrefix ] ?? null;
  const rangeMax = rangeUnit ? MAX_DELTA[ rangeUnit ] : undefined;
  const numberForRangeLabel = rangeUnit
    ? translate( `translation:number_for_range_${rangeUnit}` )
    : translate( 'translation:number_for_range' );

  /* Pour les jauges, synchronise readEnd avec readStart automatiquement (une jauge n'affiche qu'une seule valeur mais le back ne connaissant pas le type il faut envoyer le start et end)*/
  useEffect( () => {
    if ( !isGauge ) { return; }
    const category = getReadCategory( newSetting.readStart );
    const defaultEnd = category ? DEFAULT_READ_END_BY_CATEGORY[ category ] : null;
    if ( defaultEnd && newSetting.readEnd !== defaultEnd ) {
      onChangeField( 'readEnd', defaultEnd );
    }
  }, [ newSetting.readStart, newSetting.type ] );

  useEffect( () => {
    if ( rangeMax !== undefined ) {
      const current = parseInt( newSetting.deltaMinMax, 10 );
      if ( !isNaN( current ) && current > rangeMax ) {
        onChangeField( 'deltaMinMax', String( rangeMax ) );
      }
    }
  }, [ newSetting.readStart ] );

  return (
    <>
      <div className="col-span-2 mt-1">
        <p className="text-[11px] font-semibold text-gray-700 mb-1">
          {translate( 'translation:read_section' )}
        </p>
        <hr className="border-gray-200 mb-2" />
      </div>
      { !isAutoIncrementGauge && (
        <SelectInput
          className="w-full"
          label={translate( 'translation:read_start' ) as string}
          labelPosition="top"
          isRequired
          fetchOptions={fetchReadStartOptions}
          selected={newSetting.readStart}
          onSelect={val => {
            const prevCategory = getReadCategory( newSetting.readStart );
            const newCategory = getReadCategory( val as ChartReadStart );
            onChangeField( 'readStart', val );
            if ( newCategory !== prevCategory ) {
              const defaultEnd = newCategory ? DEFAULT_READ_END_BY_CATEGORY[ newCategory ] : null;
              if ( defaultEnd ) {
                onChangeField( 'readEnd', defaultEnd );
              }
            }
          }}
        />
      ) }
      { isAutoIncrementGauge && (
        <div className="col-span-2">
          <RoundToggle
            label={ translate( 'translation:display_max' ) }
            checked={ newSetting.displayMax === '1' }
            onChange={ ( isChecked: boolean ) => {
              onChangeField( 'displayMax', isChecked ? '1' : '0' );
            } }
          />
        </div>
      ) }
      { !isGauge && (
        <SelectInput
          key={getReadCategory( newSetting.readStart ) ?? 'none'}
          className="w-full"
          label={translate( 'translation:read_end' ) as string}
          labelPosition="top"
          isRequired
          isDisable={!newSetting.readStart}
          fetchOptions={fetchReadEndOptions}
          selected={newSetting.readEnd}
          onSelect={val => { return onChangeField( 'readEnd', val ); }}
        />
      ) }

      { !isAutoIncrementGauge && (
        <div className="col-span-2 mt-1">
          <div className="flex gap-3">
            <RoundToggle
              label={ translate( 'translation:display_min' ) }
              checked={ newSetting.displayMin === '1' }
              onChange={ ( isChecked: boolean ) => {
                onChangeField( 'displayMin', isChecked ? '1' : '0' );
              } }
            />
            <RoundToggle
              label={ translate( 'translation:display_max' ) }
              checked={ newSetting.displayMax === '1' }
              onChange={ ( isChecked: boolean ) => {
                onChangeField( 'displayMax', isChecked ? '1' : '0' );
              } }
            />
          </div>
          { ( newSetting.displayMin === '1' || newSetting.displayMax === '1' ) && (
            <div className="flex gap-3">
              <InputText
                className="flex-1"
                label={ numberForRangeLabel as string }
                labelPosition="top"
                type="number"
                min={0}
                max={ rangeMax }
                value={newSetting.deltaMinMax}
                onChange={val => { return onChangeField( 'deltaMinMax', val ); }}
              />
            </div>
          ) }
        </div>
      ) }
    </>
  );
};

export default ReadSection;
